import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import { LuLock, LuUser } from "react-icons/lu";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch } from "react-redux";
import { useLoginMutation } from "../../Redux/Api/Signup";
import { LoginResponse } from "../../Redux/reducer/LoginReducer";
import { consumeNextAfterLogin } from "../../utils/nextAfterLogin";
import { useSaveFcmTokenMutation } from "../../Redux/Api/Notifications";
import { requestFCMToken } from "../../utils/firebaseUtils";
import useCiT from "../../common/ci/i18n";
import { AuthField, AuthHeading, EyeToggle } from "./AuthShell";

/**
 * Sign in — email/phone + password (owner ka faisla 14-Sep: OTP login baad me).
 * Design: brand navy/orange, 50px field, poora-width orange button, loading state.
 */
export const NewLogin = ({ handleClick, signUp }: any) => {
  const t = useCiT();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [
    mutation,
    { data: signinResponse, isSuccess, isLoading, error: loginErrorResponse },
  ] = useLoginMutation();
  const [saveFcmToken] = useSaveFcmTokenMutation();

  const [loginData, setLoginData] = useState({
    emailAndPhoneNumber: "",
    password: "",
  });
  const [touched, setTouched] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  // Notification permission login ke BAAD maangte hain — pehle maangne par
  // browser ka popup login form ke upar aa jaata tha (rulebook: koi popup nahi).
  const registerFcm = async (userId?: string) => {
    if (!userId) return;
    try {
      const token: any | null = await requestFCMToken();
      if (token) saveFcmToken({ userId, token });
    } catch (e) {
      console.error("Error fetching FCM Token:", e);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value } as typeof prev));
  };

  const idError =
    touched && !loginData.emailAndPhoneNumber.trim()
      ? t("Email ya phone number daalein")
      : "";
  const pwError = touched && !loginData.password ? t("Password daalein") : "";

  const onSignIn = () => {
    setTouched(true);
    if (!loginData.emailAndPhoneNumber.trim() || !loginData.password) return;
    mutation({
      emailAndPhoneNumber: loginData.emailAndPhoneNumber.trim().toLowerCase(),
      password: loginData.password,
    });
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSignIn();
  };

  useEffect(() => {
    if (isSuccess && signinResponse) {
      const token = signinResponse?.token;
      if (token) localStorage.setItem("jwt", token);
      dispatch(LoginResponse(signinResponse?.contractorData));

      const role = signinResponse?.contractorData?.role;
      const isVerified = signinResponse?.contractorData?.isVerified;
      const isDeleted = signinResponse?.contractorData?.isDeleted;

      registerFcm(signinResponse?.contractorData?._id);

      if (role === "contractor" && isDeleted) {
        navigate("/deleted");
      } else if (role === "admin" && isVerified) {
        navigate("/admin");
      } else {
        navigate(consumeNextAfterLogin());
      }
    }
  }, [isSuccess, signinResponse, dispatch, navigate]);

  useEffect(() => {
    if (loginErrorResponse) {
      toast.error(loginErrorResponse?.data?.message || t("Login nahi ho paya"), {
        className: "bg-white text-[#182A70]",
        autoClose: 2000,
      });
    }
  }, [loginErrorResponse]);

  // Sign up tab par jaate hi purana login form khaali
  useEffect(() => {
    if (!signUp) {
      setLoginData({ emailAndPhoneNumber: "", password: "" });
      setTouched(false);
    }
  }, [signUp]);

  useEffect(() => {
    if (!location.pathname.startsWith("/forgot-password/verify-otp")) {
      localStorage.removeItem("otpTimerStart2");
    }
  }, [location.pathname]);

  return (
    <form onSubmit={onSubmit} noValidate>
      <AuthHeading
        title={t("Sign in")}
        sub={t("Order karne, rate lock karne aur apne purane order dekhne ke liye login karein.")}
      />

      <div className="flex flex-col gap-4">
        <AuthField
          label={t("Email ya phone number")}
          name="emailAndPhoneNumber"
          type="text"
          inputMode="email"
          autoComplete="username"
          placeholder="98xxxxxxxx / aap@email.com"
          icon={<LuUser size={18} />}
          value={loginData.emailAndPhoneNumber}
          onChange={handleChange}
          error={idError}
        />

        <AuthField
          label={t("Password")}
          name="password"
          type={showPassword ? "text" : "password"}
          autoComplete="current-password"
          placeholder="••••••••"
          icon={<LuLock size={18} />}
          value={loginData.password}
          onChange={handleChange}
          error={pwError}
          right={
            <EyeToggle shown={showPassword} onToggle={() => setShowPassword((p) => !p)} />
          }
        />

        <div className="-mt-1 flex justify-end">
          <Link
            to="/forgot-password/request-otp"
            className="text-[13.5px] font-semibold text-ci-navy underline-offset-2 hover:underline"
          >
            {t("Password bhool gaye?")}
          </Link>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-md border border-ci-orange bg-ci-orange text-[17px] font-bold uppercase tracking-wide text-white transition hover:border-ci-orange2 hover:bg-ci-orange2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isLoading ? (
            <>
              <FaSpinner className="animate-spin" />
              {t("Sign in ho raha hai…")}
            </>
          ) : (
            t("Sign in")
          )}
        </button>

        <p className="text-center text-[14px] text-ci-muted">
          {t("Account nahi hai?")}{" "}
          <button
            type="button"
            onClick={() => handleClick && handleClick()}
            className="font-semibold text-ci-navy underline underline-offset-2"
          >
            {t("Naya account banayein")}
          </button>
        </p>
      </div>
    </form>
  );
};

export default NewLogin;
